import { GUEST_INPUT_LIMITS } from '@repo/lib';
import { z } from '../zod.js';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import { beta } from '../beta';

const AddressPartValidationSchema = z
  .object({
    name: z.string().optional(),
    phone: z.string().optional(),
    line1: z.string().optional(),
    line2: z.string().optional(),
    city: z.string().optional(),
    region: z.string().optional().meta({
      description:
        'State, province or region. The booking RESPONSE returns this as `state`, so `state` is accepted here as an alias when round-tripping an address back in.',
    }),
    // Alias: submitted addresses use `region` (see toStoredAddress), but every
    // response — including this endpoint's own — returns `state`. Accepting
    // both stops a read-modify-write from silently dropping the value.
    state: z.string().optional().meta({
      description:
        'Alias for `region`, accepted so an address read from a booking can be posted back unchanged.',
    }),
    postalCode: z.string().optional(),
    country: z.string().length(2).meta({
      description: 'Two-letter ISO 3166-1 country code.',
      example: 'GB',
    }),
  })
  // Strict for the same reason the top-level body is: a misspelled address key
  // must fail loudly rather than be dropped from a 201.
  .strict();

/**
 * Address fields carry the structured shape the checkout pipeline expects
 * pre-persistence; `writeAddressField` later flattens it into a display string
 * plus `meta`. Every other element takes a plain string.
 */
const AddressFieldValueValidationSchema = z
  .object({
    billing: AddressPartValidationSchema.optional(),
    shipping: AddressPartValidationSchema.optional(),
    sameAsShipping: z.boolean().optional(),
  })
  .strict();

export const BookingFieldInputValidationSchema = z
  .object({
    fieldId: ObjectIdStringValidationSchema.meta({
      description: 'The ID of a field on this event. Use the event to discover field IDs.',
      example: '507f1f77bcf86cd799439011',
    }),
    value: z
      .union([z.string().max(5000), AddressFieldValueValidationSchema])
      .optional()
      .meta({
        description:
          'The submitted value. Address fields take an object of `billing` / `shipping` / `sameAsShipping`; every other field takes a string, except a multi-select field, which takes `values` instead.',
      }),
    values: z
      .array(z.string().max(5000))
      .max(100)
      .optional()
      .meta({
        description:
          "A multi-select field's chosen option values, exactly as the field defines them (an option without a value is named by its label). Labels are not accepted in place of values. Only for multi-select fields. A `value` sent beside it is ignored: the stored `value` and the labels in `meta` are rebuilt from this list.",
        example: ['google', 'facebook'],
      }),
    meta: z
      .object({
        type: z.string().min(1).meta({
          description:
            'For a tax-ID field, the tax ID type, e.g. `gb_vat` or `eu_vat`. Required for the number to be registered on the customer and for business tax treatment (reverse charge) to be applied. Without it the booking is taxed as a consumer sale.',
          example: 'gb_vat',
        }),
      })
      .optional()
      .meta({
        description:
          'Extra data for the field. Only meaningful on a tax-ID field; address fields derive their own.',
      }),
  })
  .refine(field => field.value !== undefined || field.values !== undefined, {
    message: 'Provide `value`, or `values` for a multi-select field.',
    path: ['value'],
  })
  .meta({
    id: 'BookingFieldInput',
    description:
      "A value for one of the event's fields. The field's label, element, type and reference are resolved from the event itself, so only the field ID and the value are supplied here.",
  });

export const BookingPaymentOptionInputValidationSchema = z
  .object({
    manualType: z.enum(['invoice', 'cash_on_delivery']).meta({
      description:
        'How the customer will pay. `invoice` records the booking as unpaid and awaiting an invoice payment; `cash_on_delivery` records it as unpaid and payable on arrival.',
      example: 'invoice',
    }),
  })
  // The option's name / description / instructions are taken from the event's
  // own configuration, not the caller: they surface only in CSV exports, never
  // in the invoice, the confirmation email or the booking details, so a
  // caller-supplied value is noise. Strict so passing one fails loudly.
  .strict()
  .meta({
    description:
      'How a manually settled booking is paid for. This endpoint never collects card payment: the booking is recorded as unpaid with the amount as its balance due - for a free booking that balance is zero. Settlement happens outside checkout. Omit it and pass `complimentary: true` instead to issue the tickets at no charge.',
  });

export const CreateBookingInputValidationSchema = z
  .object({
    eventId: ObjectIdStringValidationSchema.meta({
      description: 'The ID of the event to book onto.',
    }),
    tickets: z
      .record(ObjectIdStringValidationSchema, z.number().int().positive().max(1000))
      // At least one ticket, mirroring `fields`' .min(1): an empty map would
      // otherwise create a Customer (and a Stripe customer) before the
      // pipeline rejected it with "You need to select at least one ticket".
      .refine(record => Object.keys(record).length > 0, {
        message: 'At least one ticket type is required.',
      })
      .meta({
        description:
          'Ticket types to book, keyed by ticket type ID with the quantity as the value. Use the event to discover ticket type IDs. A disabled ticket type, or one in a disabled ticket group, cannot be booked.',
        example: { '65f4a1c2e4a9f3d2b1c0a9e8': 2 },
      }),
    fields: z.array(BookingFieldInputValidationSchema).min(1).max(100).meta({
      description:
        "Values for the event's fields. Must include a value for the event's email field - the booking's customer is created from it.",
    }),
    paymentOption: BookingPaymentOptionInputValidationSchema.optional(),
    complimentary: z.boolean().optional().meta({
      description:
        'Issue the tickets at no charge. The booking is recorded as `paid` with `amount`, `amountPaid` and `amountDue` of 0, `isComplimentary: true`, and the ticket lines at their face value with a matching complimentary discount. Mutually exclusive with `paymentOption` and `couponId`.',
      example: true,
    }),
    couponId: ObjectIdStringValidationSchema.optional().meta({
      description: 'A coupon to apply to the booking.',
    }),
    queryParameters: z
      .record(z.string().min(1).max(100), z.string().max(500))
      .optional()
      .refine(record => !record || Object.keys(record).length <= 50, {
        message: 'At most 50 query parameters are allowed.',
      })
      .meta({
        description:
          'Attribution or tracking parameters to record against the booking, mirroring the query string a storefront checkout captures from its page link. Stored on the booking and included in exports; not used in pricing.',
        example: { utm_source: 'box-office', utm_campaign: 'phone-sales' },
      }),
    attendees: beta(
      z
        .array(
          z
            .object({
              ticketTypeId: ObjectIdStringValidationSchema.meta({
                description:
                  'ID of the ticket type this attendee holds. Must be one of the ticket types in `tickets`.',
              }),
              name: z.string().max(GUEST_INPUT_LIMITS.name).optional(),
              email: z.string().max(GUEST_INPUT_LIMITS.email).optional(),
              answers: z
                .array(
                  z.object({
                    fieldId: z.string().meta({
                      description:
                        "ID of one of the event's `attendeeQuestions`. Any other ID, including the event's Name and Email address questions, is refused with a 400.",
                    }),
                    value: z.string().max(GUEST_INPUT_LIMITS.answerValue).optional(),
                    values: z
                      .array(z.string().max(GUEST_INPUT_LIMITS.answerValuesItem))
                      .max(GUEST_INPUT_LIMITS.answerValues)
                      .optional(),
                  }),
                )
                .max(GUEST_INPUT_LIMITS.answers)
                .optional(),
            })
            .strict(),
        )
        .max(1000)
        .optional()
        .meta({
          description:
            "Attendees for an event that collects attendee details, at most one per ticket. Each attendee names its ticket type and takes the next ticket of that type, so the order of the list doesn't matter. Tickets without an attendee are left blank, and the booking then waits for them. An attendee with answers must have a `name` or an `email`. Answer the event's `attendeeQuestions` by `id` in `answers`; the name and email address go in `name` and `email`. Ignored for events that don't collect attendee details.",
        }),
    ),
    ticketPwywAmounts: z
      .record(ObjectIdStringValidationSchema, z.number().int().nonnegative())
      .optional()
      .meta({
        description:
          'Chosen amounts for pay-what-you-want ticket types, keyed by ticket type ID, in the smallest currency unit. Required for any pay-what-you-want ticket type included in `tickets`.',
        example: { '65f4a1c2e4a9f3d2b1c0a9e8': 5000 },
      }),
  })
  // Strict, matching the other write bodies (checkout-page / event-page /
  // form-page update, subscription cancel, ticket update): a booking issues
  // tickets and emails the customer, so a misspelled key must fail loudly
  // rather than be silently dropped from a 201.
  .strict()
  .refine(input => Boolean(input.paymentOption) !== (input.complimentary === true), {
    message: 'Provide either paymentOption or complimentary: true, not both.',
    path: ['paymentOption'],
  })
  .refine(input => !(input.complimentary === true && input.couponId), {
    message: 'A coupon cannot be applied to a complimentary booking.',
    path: ['couponId'],
  })
  .meta({
    id: 'CreateBookingInput',
    description:
      'Creates an event booking without collecting card payment. With `paymentOption` the booking is recorded as unpaid and settled outside checkout; with `complimentary: true` it is recorded as paid at no charge.',
  });
